import type {
  CsvExportParams,
  ExcelExportParams,
  ProcessCellForExportParams
} from 'ag-grid-community'
import { hasProp, isDefined, isPlainObject, isString, map, pipe } from 'remeda'
import {
  EDITOR_TYPES,
  type EditorType,
  GRID_DEFAULTS,
  TOOLTIP_DEFAULTS
} from '../constants'
import type {
  EditorParamValue,
  GridColumn,
  GridEditorParams,
  GridLeafColumn
} from './column-processors'
import { createProcessCellCallback } from './export-converters'
import { type GridSort, normalizeGridSorting } from './sorting-state'
import { parseJsonSafely } from './transforms'

type RowMenuItem = {
  readonly label: string
  readonly icon?: string
  readonly action: string
  readonly disabled?: boolean
  readonly separator?: boolean
}

type RowMenuConfig = {
  readonly items: readonly RowMenuItem[]
}

type GridMeta = {
  readonly current_page: number
  readonly next_page: number | null
  readonly page_size: number
  readonly total_count: number
  readonly total_pages: number
}

type MasterDetailConfig = {
  readonly masterColumn: string
  readonly detailsField: string
  readonly columns: readonly GridColumn[]
}

type GridEvents = {
  readonly onProgressiveLoad?: string
  readonly onColumnsUpdate?: string
  readonly onEdited?: string
  readonly onBuild?: string
  readonly onRowMoved?: string
  readonly onRowMenu?: string
}

type GridConfigInput = {
  readonly sortingTarget?: string
  readonly sorting?: readonly GridSort[]
  readonly columns?: readonly GridColumn[]
  readonly data?: readonly Record<string, unknown>[]
  readonly meta?: GridMeta
  readonly paginationSize?: number
  readonly readOnly?: boolean
  readonly selectableRows?: boolean
  readonly movableColumns?: boolean
  readonly movableRows?: boolean
  readonly rowMenu?: RowMenuConfig
  readonly masterDetail?: MasterDetailConfig
  readonly domLayout?: 'normal' | 'autoHeight' | 'print'
  readonly events?: GridEvents
}

type GridElementData = {
  readonly config?: string
}

type GridConfiguration = {
  readonly id: string
  readonly target: string | null
  readonly config: GridConfigInput
  readonly events: GridEvents
  readonly isAsync: boolean
  readonly paginationSize: number
  readonly readonly: boolean
  readonly selectableRows: boolean
  readonly movableColumns: boolean
  readonly movableRows: boolean
  readonly rowMenu?: RowMenuConfig
  readonly domLayout: 'normal' | 'autoHeight' | 'print'
}

type GridHTMLElement = HTMLElement & {
  readonly dataset: DOMStringMap & GridElementData
  readonly id: string
}

type TooltipConfig = {
  readonly tooltipShowDelay: number
  readonly tooltipHideDelay: number
  readonly tooltipInteraction: boolean
}

type GridLeafBoundary = Readonly<Record<string, unknown>> & {
  readonly field: string
  readonly title: string
  readonly visible: boolean
}

type GridGroupBoundary = Readonly<Record<string, unknown>> & {
  readonly title: string
  readonly children: readonly unknown[]
}

const EDITOR_VALUES: ReadonlySet<string> = new Set(Object.values(EDITOR_TYPES))
const EDITOR_PARAM_KEYS: ReadonlySet<string> = new Set([
  'multiple',
  'required',
  'min',
  'max',
  'precision',
  'values',
  'loadEvent',
  'loadParams',
  'pageSize',
  'timeout',
  'country',
  'locale',
  'onlyCountries'
] as const satisfies readonly (keyof GridEditorParams)[])

const isOptionalProperty = (
  value: Readonly<Record<PropertyKey, unknown>>,
  key: string,
  predicate: (property: unknown) => boolean
): boolean =>
  value[key] === undefined || value[key] === null || predicate(value[key])

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const isNumericString = (value: unknown): value is string =>
  isString(value) && value.trim().length > 0 && Number.isFinite(Number(value))

const normalizeWidth = (value: unknown): number | undefined =>
  isFiniteNumber(value)
    ? value
    : isNumericString(value)
      ? Number(value)
      : undefined

const isPositiveInteger = (value: unknown): value is number =>
  isFiniteNumber(value) && Number.isSafeInteger(value) && value > 0

const isBoolean = (value: unknown): value is boolean =>
  typeof value === 'boolean'

const isStringOrNumber = (value: unknown): value is string | number =>
  isString(value) || isFiniteNumber(value)

const isCountryList = (value: unknown): value is string | readonly string[] =>
  isString(value) ||
  (Array.isArray(value) && value.every((country) => isString(country)))

const normalizeArray = <Output>(
  value: unknown,
  normalize: (item: unknown) => Output | undefined
): readonly Output[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined
  }

  const items = pipe(value, map(normalize))
  return items.every(isDefined) ? items : undefined
}

const isEditorType = (value: unknown): value is EditorType =>
  isString(value) && EDITOR_VALUES.has(value)

const isEditorScalar = (value: unknown): value is EditorParamValue['value'] =>
  isString(value) ||
  typeof value === 'boolean' ||
  (typeof value === 'number' && Number.isFinite(value))

const normalizeEditorParamValue = (
  value: unknown
): EditorParamValue | undefined => {
  if (
    !(
      isPlainObject(value) &&
      hasProp(value, 'value') &&
      isEditorScalar(value.value) &&
      isOptionalProperty(value, 'label', isString) &&
      isOptionalProperty(value, 'title', isString) &&
      isOptionalProperty(value, 'icon', isString) &&
      isOptionalProperty(value, 'color', isString)
    )
  ) {
    return undefined
  }

  return {
    value: value.value,
    ...(isString(value.label) ? { label: value.label } : {}),
    ...(isString(value.title) ? { title: value.title } : {}),
    ...(isString(value.icon) ? { icon: value.icon } : {}),
    ...(isString(value.color) ? { color: value.color } : {})
  }
}

const normalizeEditorParamValues = (
  value: unknown
): readonly EditorParamValue[] | undefined =>
  normalizeArray(value, normalizeEditorParamValue)

const isPrecision = (
  value: unknown
): value is NonNullable<GridEditorParams['precision']> =>
  value === 'minute' ||
  value === 'second' ||
  value === 'millisecond' ||
  isFiniteNumber(value)

const normalizeGridEditorParams = (
  value: unknown
): GridEditorParams | undefined => {
  if (
    !(
      isPlainObject(value) &&
      Object.keys(value).every((key) => EDITOR_PARAM_KEYS.has(key)) &&
      isOptionalProperty(value, 'multiple', isBoolean) &&
      isOptionalProperty(value, 'required', isBoolean) &&
      isOptionalProperty(value, 'min', isStringOrNumber) &&
      isOptionalProperty(value, 'max', isStringOrNumber) &&
      isOptionalProperty(value, 'precision', isPrecision) &&
      isOptionalProperty(value, 'loadEvent', isString) &&
      isOptionalProperty(value, 'loadParams', isPlainObject) &&
      isOptionalProperty(value, 'pageSize', isPositiveInteger) &&
      isOptionalProperty(value, 'timeout', isPositiveInteger) &&
      isOptionalProperty(value, 'country', isString) &&
      isOptionalProperty(value, 'locale', isString) &&
      isOptionalProperty(value, 'onlyCountries', isCountryList)
    )
  ) {
    return undefined
  }

  const values =
    value.values === undefined || value.values === null
      ? undefined
      : normalizeEditorParamValues(value.values)

  if (value.values != null && !values) {
    return undefined
  }

  return {
    ...(typeof value.multiple === 'boolean'
      ? { multiple: value.multiple }
      : {}),
    ...(typeof value.required === 'boolean'
      ? { required: value.required }
      : {}),
    ...(isStringOrNumber(value.min) ? { min: value.min } : {}),
    ...(isStringOrNumber(value.max) ? { max: value.max } : {}),
    ...(isPrecision(value.precision) ? { precision: value.precision } : {}),
    ...(values ? { values } : {}),
    ...(isString(value.loadEvent) ? { loadEvent: value.loadEvent } : {}),
    ...(isPlainObject(value.loadParams)
      ? { loadParams: value.loadParams }
      : {}),
    ...(isPositiveInteger(value.pageSize) ? { pageSize: value.pageSize } : {}),
    ...(isPositiveInteger(value.timeout) ? { timeout: value.timeout } : {}),
    ...(isString(value.country) ? { country: value.country } : {}),
    ...(isString(value.locale) ? { locale: value.locale } : {}),
    ...(isCountryList(value.onlyCountries)
      ? { onlyCountries: value.onlyCountries }
      : {})
  }
}

const isGridLeafBoundary = (value: unknown): value is GridLeafBoundary =>
  isPlainObject(value) &&
  isString(value.field) &&
  isString(value.title) &&
  typeof value.visible === 'boolean'

const isGridGroupBoundary = (value: unknown): value is GridGroupBoundary =>
  isPlainObject(value) && isString(value.title) && Array.isArray(value.children)

const normalizeTitleFormatterParams = (
  value: unknown
): GridLeafColumn['titleFormatterParams'] =>
  isPlainObject(value)
    ? {
        ...(isString(value.icon) ? { icon: value.icon } : {})
      }
    : undefined

const normalizeBottomCalcParams = (
  value: unknown
): GridLeafColumn['bottomCalcParams'] => {
  if (!isPlainObject(value)) {
    return undefined
  }

  return isStringOrNumber(value.value) ? { value: value.value } : {}
}

const normalizeBottomCalcFormatterParams = (
  value: unknown
): GridLeafColumn['bottomCalcFormatterParams'] =>
  isPlainObject(value)
    ? {
        ...(isString(value.tooltip) || value.tooltip === null
          ? { tooltip: value.tooltip }
          : {})
      }
    : undefined

const normalizeGridColumn = (value: unknown): GridColumn | undefined => {
  if (isGridGroupBoundary(value)) {
    const children = normalizeGridColumns(value.children)
    return children
      ? {
          title: value.title,
          children,
          ...(isString(value.groupId) ? { groupId: value.groupId } : {})
        }
      : undefined
  }

  if (!isGridLeafBoundary(value)) {
    return undefined
  }

  const editor = isEditorType(value.editor) ? value.editor : undefined
  const editorParams =
    value.editorParams === undefined || value.editorParams === null
      ? undefined
      : normalizeGridEditorParams(value.editorParams)
  const validEditor =
    editor !== undefined &&
    (value.editorParams == null || editorParams !== undefined)
  const width = normalizeWidth(value.width)
  const minWidth = normalizeWidth(value.minWidth)
  const maxWidth = normalizeWidth(value.maxWidth)
  const flex = normalizeWidth(value.flex)
  const titleFormatterParams = normalizeTitleFormatterParams(
    value.titleFormatterParams
  )
  const bottomCalcParams = normalizeBottomCalcParams(value.bottomCalcParams)
  const bottomCalcFormatterParams = normalizeBottomCalcFormatterParams(
    value.bottomCalcFormatterParams
  )
  const normalizedColumn: GridLeafColumn = {
    field: value.field,
    ...(typeof value.sortField === 'string' && value.sortField.length > 0
      ? { sortField: value.sortField }
      : {}),
    title: value.title,
    visible: value.visible,
    formatter: isString(value.formatter) ? value.formatter : 'plaintext',
    ...(value.align === 'right' ? { align: value.align } : {}),
    ...(width === undefined ? {} : { width }),
    ...(minWidth === undefined ? {} : { minWidth }),
    ...(maxWidth === undefined ? {} : { maxWidth }),
    ...(flex === undefined ? {} : { flex }),
    ...(value.pinned === 'left' || value.pinned === 'right'
      ? { pinned: value.pinned }
      : {}),
    ...(typeof value.editable === 'boolean'
      ? { editable: value.editable }
      : {}),
    ...(titleFormatterParams ? { titleFormatterParams } : {}),
    ...(isString(value.bottomCalc) ? { bottomCalc: value.bottomCalc } : {}),
    ...(bottomCalcParams ? { bottomCalcParams } : {}),
    ...(bottomCalcFormatterParams ? { bottomCalcFormatterParams } : {})
  }

  return validEditor
    ? {
        ...normalizedColumn,
        editor,
        ...(editorParams ? { editorParams } : {})
      }
    : normalizedColumn
}

export const normalizeGridColumns = (
  value: unknown
): readonly GridColumn[] | undefined =>
  normalizeArray(value, normalizeGridColumn)

const normalizeRowMenuItem = (value: unknown): RowMenuItem | undefined =>
  isPlainObject(value) && isString(value.label) && isString(value.action)
    ? {
        label: value.label,
        action: value.action,
        ...(isString(value.icon) ? { icon: value.icon } : {}),
        ...(typeof value.disabled === 'boolean'
          ? { disabled: value.disabled }
          : {}),
        ...(typeof value.separator === 'boolean'
          ? { separator: value.separator }
          : {})
      }
    : undefined

const normalizeRowMenu = (value: unknown): RowMenuConfig | undefined => {
  if (!(isPlainObject(value) && Array.isArray(value.items))) {
    return undefined
  }

  const items = normalizeArray(value.items, normalizeRowMenuItem)
  return items ? { items } : undefined
}

const normalizeMasterDetail = (
  value: unknown
): MasterDetailConfig | undefined => {
  if (
    !(
      isPlainObject(value) &&
      isString(value.masterColumn) &&
      isString(value.detailsField)
    )
  ) {
    return undefined
  }

  const columns = normalizeGridColumns(value.columns)
  return columns
    ? {
        masterColumn: value.masterColumn,
        detailsField: value.detailsField,
        columns
      }
    : undefined
}

const normalizeMeta = (value: unknown): GridMeta | undefined =>
  isPlainObject(value) &&
  isPositiveInteger(value.current_page) &&
  (value.next_page === null || isPositiveInteger(value.next_page)) &&
  isPositiveInteger(value.page_size) &&
  isFiniteNumber(value.total_count) &&
  Number.isSafeInteger(value.total_count) &&
  value.total_count >= 0 &&
  isFiniteNumber(value.total_pages) &&
  Number.isSafeInteger(value.total_pages) &&
  value.total_pages >= 0
    ? {
        current_page: value.current_page,
        next_page: value.next_page,
        page_size: value.page_size,
        total_count: value.total_count,
        total_pages: value.total_pages
      }
    : undefined

const normalizeEvents = (value: unknown): GridEvents | undefined =>
  isPlainObject(value)
    ? {
        ...(isString(value.onProgressiveLoad)
          ? { onProgressiveLoad: value.onProgressiveLoad }
          : {}),
        ...(isString(value.onColumnsUpdate)
          ? { onColumnsUpdate: value.onColumnsUpdate }
          : {}),
        ...(isString(value.onEdited) ? { onEdited: value.onEdited } : {}),
        ...(isString(value.onBuild) ? { onBuild: value.onBuild } : {}),
        ...(isString(value.onRowMoved) ? { onRowMoved: value.onRowMoved } : {}),
        ...(isString(value.onRowMenu) ? { onRowMenu: value.onRowMenu } : {})
      }
    : undefined

const normalizeData = (
  value: unknown
): readonly Record<string, unknown>[] | undefined =>
  normalizeArray(value, (row) => (isPlainObject(row) ? row : undefined))

/** Validate the serialized page size without coercing malformed values. */
export const validatePaginationSize = (size: unknown): number => {
  if (
    typeof size !== 'number' ||
    !Number.isInteger(size) ||
    size <= 0 ||
    size > 1000
  ) {
    console.warn(
      `Invalid pagination size, using default ${GRID_DEFAULTS.PAGINATION_SIZE}:`,
      size
    )
    return GRID_DEFAULTS.PAGINATION_SIZE
  }

  return size
}

/** Normalize the server configuration at the grid boundary. */
export const normalizeGridConfig = (
  value: unknown
): GridConfigInput | undefined => {
  if (!isPlainObject(value)) {
    return undefined
  }

  const columns = normalizeGridColumns(value.columns ?? [])

  if (!columns) {
    return undefined
  }

  const data = normalizeData(value.data)
  const meta = normalizeMeta(value.meta)
  const events = normalizeEvents(value.events)
  const rowMenu = normalizeRowMenu(value.rowMenu)
  const masterDetail = normalizeMasterDetail(value.masterDetail)
  const sorting = normalizeGridSorting(value.sorting)

  return {
    columns,
    ...(typeof value.sortingTarget === 'string' &&
    value.sortingTarget.trim().length > 0
      ? { sortingTarget: value.sortingTarget }
      : {}),
    ...(sorting ? { sorting } : {}),
    ...(data ? { data } : {}),
    ...(meta ? { meta } : {}),
    ...(value.paginationSize === undefined
      ? {}
      : { paginationSize: validatePaginationSize(value.paginationSize) }),
    ...(events ? { events } : {}),
    ...(rowMenu ? { rowMenu } : {}),
    ...(masterDetail ? { masterDetail } : {}),
    ...(typeof value.readOnly === 'boolean'
      ? { readOnly: value.readOnly }
      : {}),
    ...(typeof value.selectableRows === 'boolean'
      ? { selectableRows: value.selectableRows }
      : {}),
    ...(typeof value.movableColumns === 'boolean'
      ? { movableColumns: value.movableColumns }
      : {}),
    ...(typeof value.movableRows === 'boolean'
      ? { movableRows: value.movableRows }
      : {}),
    ...(value.domLayout === 'normal' ||
    value.domLayout === 'autoHeight' ||
    value.domLayout === 'print'
      ? { domLayout: value.domLayout }
      : {})
  }
}

export const extractConfigFromElement = (
  element: GridHTMLElement
): GridConfigInput =>
  pipe(
    element.dataset.config ?? '{}',
    parseJsonSafely,
    normalizeGridConfig,
    (config) => config ?? {}
  )

export const getElementId = (element: GridHTMLElement): string => element.id

export const getPhxTarget = (element: HTMLElement): string | null =>
  element.getAttribute('phx-target')

export const createTooltipConfig = (): TooltipConfig => ({
  tooltipShowDelay: TOOLTIP_DEFAULTS.SHOW_DELAY,
  tooltipHideDelay: TOOLTIP_DEFAULTS.HIDE_DELAY,
  tooltipInteraction: TOOLTIP_DEFAULTS.INTERACTION_ENABLED
})

const extractBooleanConfig = <T extends Record<string, unknown>>(
  config: T,
  key: keyof T
): boolean => Boolean(config[key])

const validateGridConfig = (context: GridConfiguration): void => {
  if (context.readonly && context.selectableRows) {
    console.warn(
      'Grid configured as readonly but selectableRows is enabled. Selection will be disabled in readonly mode.',
      context.id
    )
  }
}

export const isAsyncMode = (onProgressiveLoad?: string): boolean =>
  Boolean(onProgressiveLoad && onProgressiveLoad.trim().length > 0)

/** Share one normalized configuration across grid options and row loading. */
export const createGridContext = (
  element: GridHTMLElement
): GridConfiguration => {
  const config = extractConfigFromElement(element)
  const events = config.events ?? {}
  const onProgressiveLoad = events.onProgressiveLoad

  const context: GridConfiguration = {
    id: getElementId(element),
    target: getPhxTarget(element),
    config,
    events: {
      onProgressiveLoad,
      onColumnsUpdate: events.onColumnsUpdate,
      onEdited: events.onEdited,
      onBuild: events.onBuild,
      onRowMoved: events.onRowMoved,
      onRowMenu: events.onRowMenu
    },
    isAsync: isAsyncMode(onProgressiveLoad),
    paginationSize: config.paginationSize ?? GRID_DEFAULTS.PAGINATION_SIZE,
    readonly: extractBooleanConfig(config, 'readOnly'),
    selectableRows: extractBooleanConfig(config, 'selectableRows'),
    movableColumns: extractBooleanConfig(config, 'movableColumns'),
    movableRows: extractBooleanConfig(config, 'movableRows'),
    rowMenu: config.rowMenu,
    domLayout: config.domLayout ?? 'normal'
  }

  validateGridConfig(context)

  return context
}

export const calculatePageNumber = (
  startRow: number,
  pageSize: number
): number => Math.floor(startRow / pageSize) + 1

export const createExportConfig = (): CsvExportParams & ExcelExportParams => ({
  processCellCallback: createProcessCellCallback()
})

export const createClipboardConfig = (): {
  processCellForClipboard: (params: ProcessCellForExportParams) => string
  processCellFromClipboard: (params: ProcessCellForExportParams) => string
} => ({
  processCellForClipboard: createProcessCellCallback(),
  processCellFromClipboard: (params: ProcessCellForExportParams) =>
    String(params.value ?? '')
})

export type { ThemeDefaultParams } from 'ag-grid-community'
export { gridTheme } from './theme-config'
export type {
  GridConfigInput,
  GridConfiguration,
  GridElementData,
  GridEvents,
  GridHTMLElement,
  MasterDetailConfig,
  RowMenuConfig,
  RowMenuItem,
  TooltipConfig
}
